import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { RootState } from '../../store/store';
import {
  setProfileDefault,
  setCurvePreset,
} from '../../store/slices/missionPlannerSlice';
import { useRouteMetrics } from '../../hooks/useRouteMetrics';
import { CAPABILITY, ProfileAxis } from '../../utils/profile';
import { CurvePreset } from '../../types/missionPlanner';
import { ProfileChart } from './ProfileChart';

const PRESETS: { value: CurvePreset; label: string }[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'smooth', label: 'Smooth' },
  { value: 'stepped', label: 'Stepped' },
];

/** Global default number input for one axis, clamped in the slice. */
const DefaultInput: React.FC<{ axis: ProfileAxis; value: number }> = ({
  axis,
  value,
}) => {
  const dispatch = useDispatch();
  const cap = CAPABILITY[axis];
  // Local text so typing isn't fought by the clamped store value.
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);

  return (
    <TextField
      size="small"
      type="number"
      label={`Default ${cap.label.toLowerCase()} (${cap.unit})`}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const v = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(v)) {
          dispatch(setProfileDefault({ axis, value: v }));
        }
      }}
      inputProps={{
        min: cap.min,
        max: cap.max,
        'aria-label': `Default ${cap.label}`,
      }}
      sx={{ flex: 1 }}
    />
  );
};

/** Interpolation preset selector for one axis. */
const PresetSelect: React.FC<{ axis: ProfileAxis; value: CurvePreset }> = ({
  axis,
  value,
}) => {
  const dispatch = useDispatch();
  return (
    <TextField
      select
      size="small"
      label={`${CAPABILITY[axis].label} curve`}
      value={value}
      onChange={(e) =>
        dispatch(
          setCurvePreset({ axis, preset: e.target.value as CurvePreset })
        )
      }
      sx={{ minWidth: 130 }}
    >
      {PRESETS.map((p) => (
        <MenuItem key={p.value} value={p.value}>
          {p.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * Speed & altitude profile editor (issue #298): global defaults, per-axis
 * interpolation preset, and an interactive curve chart per axis over the route
 * distance. Per-segment overrides are expressed as control points on the curve.
 */
export const ProfilePanel: React.FC = () => {
  const profile = useSelector(
    (state: RootState) => state.missionPlanner.draft.profile
  );
  const distanceUnit = useSelector(
    (state: RootState) => state.missionPlanner.distanceUnit
  );

  // The profile spans the whole flown route, closing leg included (#404).
  const { totalKm } = useRouteMetrics();

  return (
    <Box
      component="section"
      aria-label="Speed and altitude profile"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography variant="subtitle2" component="h3">
        Speed &amp; altitude
      </Typography>

      <Stack direction="row" spacing={1}>
        <DefaultInput axis="altitude" value={profile.altitudeM} />
        <DefaultInput axis="speed" value={profile.cruiseSpeedMs} />
      </Stack>

      {totalKm === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Add at least two waypoints to shape the altitude and speed profiles
          along the route.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            Click a chart to add a control point, drag to adjust, double-click to
            remove. Distance axis in km ({distanceUnit} used elsewhere).
          </Typography>

          <PresetSelect axis="altitude" value={profile.altitudeCurve.preset} />
          <ProfileChart
            axis="altitude"
            xMaxKm={totalKm}
            defaultValue={profile.altitudeM}
          />

          <PresetSelect axis="speed" value={profile.speedCurve.preset} />
          <ProfileChart
            axis="speed"
            xMaxKm={totalKm}
            defaultValue={profile.cruiseSpeedMs}
          />
        </>
      )}
    </Box>
  );
};
